enum AdminPermissions {
  announcement = 'announcement',
  calendar = 'calendar',
  schoolAdmin = 'schoolAdmin',
  addPeople = 'addPeople',
  classTeacher = 'classTeacher',
  addSubject = 'addSubject',
  previousExams = 'previousExams',
  weeklyPlanner = 'weeklyPlanner',
  divTransfer = 'divTransfer',
  schoolSettings = 'schoolSettings',
  promotions = 'promotions',
  //   gradingSystem = 'gradingSystem',
  teacherAttendance = 'teacherAttendance',
  teacherAttendanceSheet = 'teacherAttendanceSheet',
  teacherUpdateAttendance = 'teacherUpdateAttendance',
  disciplineRecord = 'disciplineRecord',
  schoolFees = 'schoolFees',
  cashDeposit = 'cashDeposit',
  leaves = 'leaves',
  reports = 'reports',
}

import {
  Bell,
  BookCopy,
  BookOpen,
  Calendar,
  CalendarCheck,
  CalendarClock,
  ClipboardList,
  Coins,
  FileText,
  GraduationCap,
  Home,
  Receipt,
  School,
  Sheet,
  UserPlus,
  Users,
} from 'lucide-react';
import type { RouteConfig } from '../types';
import {
  AddAdminBulk,
  AddSchoolAdmin,
  AddSchoolStudent,
  AddSchoolTeacher,
  // AddStaffBulk,
  AddStudentBulk,
  AddSubject,
  AddTeacherBulk,
  AdminAdmissions,
  AdminAnnouncements,
  AdminDashboard,
  AdminReports,
  RankingReport,
  DuplicateStudents,
  AdminSchoolCalendar,
  AdminSoa,
  // AdminStaffLeaves,
  AdminStudentLeaves,
  AdminTeacherAttendanceSheet,
  AdminTeacherLeaves,
  //   AdminTeacherPayroll,
  AdminTeacherTakeAttendance,
  AdminTeacherUpdateAttendance,
  AdminWeeklyPlanner,
  AdminStandardWeeklyPlanner,
  AdminHomeworkPlanner,
  AdminStandardHomeworkPlanner,
  AdminStudentSyllabus,
  AdminStandardSyllabus,
  AdminTimeTable,
  AssignClassTeacher,
  CashDeposit,
  // DefineRates,
  DisciplineRecords,
  // FeeStructure,
  PendingFees,
  PreviousExams,
  SchoolSetting,
  StudentDivisionTransfer,
  StudentPromotion,
  TakeAttendance,
  TempPage,
  UserProfile,
  AdminProfile,
  GradingSystem,
  AdminReportCard,
  AdminLibrary,
  AdminInventory,
  //   AdminLibraryImport,
  AdminDebug,
} from './lazy-components';
import AdminTempCodes from '@/pages/admin/temp-codes/admin-temp-codes';

const isDevEnvironment = import.meta.env.DEV;

export const schoolAdminRoutes: RouteConfig[] = [
  {
    isLabel: true,
    name: 'Home',
    showOnSideBar: true,
  },
  {
    path: '/admin/dashboard',
    name: 'Dashboard',
    icon: Home,
    showOnSideBar: true,
    component: AdminDashboard,
    isCombobox: true,
    breadcrumb: {
      title: 'Dashboard',
      parent: null,
    },
  },
  ...(isDevEnvironment
    ? [
        {
          path: '/admin/testing',
          name: 'Admin Testing',
          icon: FileText,
          showOnSideBar: true,
          component: AdminDebug,
          isCombobox: true,
          breadcrumb: {
            title: 'Admin Testing',
            parent: '/admin/dashboard',
          },
        },
      ]
    : []),
  {
    path: '/school-admin/attendance/:standard/:division',
    showOnSideBar: false,
    name: 'Attendance',
    icon: Calendar,
    component: TakeAttendance,
    isCombobox: false,
    breadcrumb: {
      title: 'Attendance',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/profile/',
    name: 'Profile',
    showOnSideBar: false,
    component: AdminProfile,
    isCombobox: false,
    breadcrumb: {
      title: 'Profile',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/users/:username/',
    name: 'User Profile',
    showOnSideBar: false,
    component: UserProfile,
    isCombobox: false,
    breadcrumb: {
      title: 'User Profile',
      parent: '/admin/dashboard',
    },
  },
  {
    isLabel: true,
    name: 'Administratation',
    showOnSideBar: true,
  },
  {
    path: '/administratation/announcements/',
    name: 'Announcements',
    icon: Bell,
    showOnSideBar: true,
    component: AdminAnnouncements,
    isCombobox: true,
    permission: AdminPermissions.announcement,
    breadcrumb: {
      title: 'Announcements',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/school-calendar/',
    name: 'School Calendar',
    icon: Calendar,
    showOnSideBar: true,
    component: AdminSchoolCalendar,
    isCombobox: true,
    permission: AdminPermissions.calendar,
    breadcrumb: {
      title: 'School Calendar',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/admission/',
    name: 'Admissions',
    icon: FileText,
    showOnSideBar: true,
    component: AdminAdmissions,
    isCombobox: true,
    breadcrumb: {
      title: 'Admissions',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/administratation/duplicate-students/',
    name: 'Duplicate Students',
    icon: Users,
    showOnSideBar: true,
    component: DuplicateStudents,
    isCombobox: true,
    permission: AdminPermissions.reports,
    breadcrumb: {
      title: 'Duplicate Students',
      parent: '/admin/dashboard',
    },
  },
  {
    isGroup: true,
    groupName: 'School Admin',
    name: 'School Admin',
    icon: Users,
    showOnSideBar: true,
    state: 'schooladminpages',
    permission: AdminPermissions.schoolAdmin,
    children: [
      {
        path: '/school-admin/add-school-admin/',
        icon: UserPlus,
        name: 'Add School Admin',
        showOnSideBar: true,
        component: AddSchoolAdmin,
        isCombobox: true,
        breadcrumb: {
          title: 'Add School Admin',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/school-admin/add-school-admin-csv/',
        icon: FileText,
        name: 'Add Admin CSV File',
        showOnSideBar: true,
        component: AddAdminBulk,
        isCombobox: true,
        breadcrumb: {
          title: 'Add Admin CSV File',
          parent: '/admin/dashboard',
        },
      },
      // {
      //   path: '/school-admin/add-school-staff-csv/',
      //   icon: FileText,
      //   name: 'Add Staff CSV File',
      //   showOnSideBar: true,
      //   component: AddStaffBulk,
      //   isCombobox: true,
      //   breadcrumb: {
      //     title: 'Add Staff CSV File',
      //     parent: '/admin/dashboard',
      //   },
      // },
    ],
  },
  {
    isGroup: true,
    groupName: 'Add People',
    name: 'Add People',
    icon: UserPlus,
    showOnSideBar: true,
    state: 'addpeoplepages',
    permission: AdminPermissions.addPeople,
    children: [
      {
        path: '/add-people/add-teachers/',
        icon: UserPlus,
        name: 'Add Teachers',
        showOnSideBar: true,
        component: AddSchoolTeacher,
        isCombobox: true,
        breadcrumb: {
          title: 'Add Teachers',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/add-people/add-students/',
        icon: UserPlus,
        name: 'Add Students',
        showOnSideBar: true,
        component: AddSchoolStudent,
        isCombobox: true,
        breadcrumb: {
          title: 'Add Students',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/add-people/add-students-csv/',
        icon: FileText,
        name: 'Add Students CSV/FILE',
        showOnSideBar: true,
        component: AddStudentBulk,
        isCombobox: true,
        breadcrumb: {
          title: 'Add Students CSV/FILE',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/add-people/add-teachers-csv/',
        icon: FileText,
        name: 'Add Teachers CSV/FILE',
        showOnSideBar: true,
        component: AddTeacherBulk,
        isCombobox: true,
        breadcrumb: {
          title: 'Add Teachers CSV/FILE',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/add-people/temp-passwords/',
        icon: FileText,
        name: 'Temporary Passwords',
        showOnSideBar: true,
        component: AdminTempCodes,
        isCombobox: true,
        breadcrumb: {
          title: 'Temporary Passwords',
          parent: '/admin/dashboard',
        },
      },
      // {
      //   path: '/add-people/add-staff/',
      //   icon: UserPlus,
      //   name: 'Add Staff',
      //   showOnSideBar: true,
      //   component: TempPage,
      //   isCombobox: false,
      //   breadcrumb: {
      //     title: 'Add Staff',
      //     parent: '/admin/dashboard',
      //   },
      // },
    ],
  },
  {
    isLabel: true,
    name: 'Academics',
    showOnSideBar: true,
  },
  {
    path: '/edit-class-teacher/:classTeacherId',
    name: 'Edit Class Teacher',
    showOnSideBar: false,
    component: TempPage,
    isCombobox: false,
    permission: AdminPermissions.classTeacher,
    breadcrumb: {
      title: 'Edit Class Teacher',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/edit-subject/:subjectId',
    name: 'Edit Subject',
    showOnSideBar: false,
    component: TempPage,
    isCombobox: false,
    permission: AdminPermissions.addSubject,
    breadcrumb: {
      title: 'Edit Subject',
      parent: '/admin/dashboard',
    },
  },
  {
    isGroup: true,
    groupName: 'Academics',
    name: 'Academics',
    icon: School,
    showOnSideBar: true,
    state: 'classroompages',
    children: [
      {
        path: '/academics/classroom/add-class-teacher/',
        icon: School,
        name: 'Class Teacher',
        showOnSideBar: true,
        component: AssignClassTeacher,
        isCombobox: true,
        permission: AdminPermissions.classTeacher,
        breadcrumb: {
          title: 'Class Teacher',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/add-subject/',
        icon: BookCopy,
        name: 'Add Subject',
        showOnSideBar: true,
        component: AddSubject,
        isCombobox: false,
        permission: AdminPermissions.addSubject,
        breadcrumb: {
          title: 'Add Subject',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/time-table/',
        icon: CalendarClock,
        name: 'Time Table',
        showOnSideBar: true,
        component: AdminTimeTable,
        isCombobox: true,
        breadcrumb: {
          title: 'Time Table',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/previous-exams/',
        icon: FileText,
        name: 'Previous Exams',
        showOnSideBar: true,
        component: PreviousExams,
        isCombobox: true,
        permission: AdminPermissions.previousExams,
        breadcrumb: {
          title: 'Previous Exams',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/weekly-planner/',
        icon: Calendar,
        name: 'Weekly Planner',
        showOnSideBar: true,
        component: AdminWeeklyPlanner,
        isCombobox: true,
        permission: AdminPermissions.weeklyPlanner,
        breadcrumb: {
          title: 'Weekly Planner',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/weekly-planner/:standard/',
        name: 'Standard Weekly Planner',
        showOnSideBar: false,
        component: AdminStandardWeeklyPlanner,
        isCombobox: false,
        breadcrumb: {
          title: 'Standard Weekly Planner',
          parent: '/academics/classroom/weekly-planner/',
        },
      },
      {
        path: '/academics/classroom/homework-planner/',
        icon: Calendar,
        name: 'Homework Planner',
        showOnSideBar: true,
        component: AdminHomeworkPlanner,
        isCombobox: true,
        breadcrumb: {
          title: 'Homework Planner',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/homework-planner/:standard/',
        name: 'Standard Homework Planner',
        showOnSideBar: false,
        component: AdminStandardHomeworkPlanner,
        isCombobox: false,
        breadcrumb: {
          title: 'Standard Homework Planner',
          parent: '/academics/classroom/homework-planner/',
        },
      },
      {
        path: '/academics/classroom/syllabus/',
        icon: Calendar,
        name: 'Syllabus',
        showOnSideBar: true,
        component: AdminStudentSyllabus,
        isCombobox: true,
        breadcrumb: {
          title: 'Syllabus',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/syllabus/:standard/',
        name: 'Standard Syllabus',
        showOnSideBar: false,
        component: AdminStandardSyllabus,
        isCombobox: false,
        breadcrumb: {
          title: 'Standard Syllabus',
          parent: '/academics/classroom/syllabus/',
        },
      },
      // THIS WILL BE A ROUTE FOR MOVING LIKE STUDENTS BETWEEN DIVISIONS , need some better name
      {
        path: '/academics/classroom/student-transfer/',
        icon: UserPlus,
        name: 'Student Division  Transfer',
        showOnSideBar: true,
        component: StudentDivisionTransfer,
        permission: AdminPermissions.divTransfer,
        isCombobox: true,
        breadcrumb: {
          title: 'Student Division Transfer',
          parent: '/admin/dashboard',
        },
      },

      {
        path: '/academics/classroom/school-setting/',
        icon: School,
        name: 'School Setting',
        showOnSideBar: true,
        component: SchoolSetting,
        isCombobox: false,
        permission: AdminPermissions.schoolSettings,
        breadcrumb: {
          title: 'School Setting',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/assign-standard-division/',
        icon: GraduationCap,
        name: 'Student Promotion',
        showOnSideBar: true,
        component: StudentPromotion,
        isCombobox: false,
        permission: AdminPermissions.promotions,
        breadcrumb: {
          title: 'Student Promotion',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/grading-system/',
        icon: GraduationCap,
        name: 'Grading System',
        showOnSideBar: true,
        component: GradingSystem,
        isCombobox: false,
        // permission: AdminPermissions.gradingSystem,
        breadcrumb: {
          title: 'Grading System',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/academics/classroom/report-card/',
        icon: FileText,
        name: 'Report Cards',
        showOnSideBar: true,
        component: AdminReportCard,
        isCombobox: false,
        breadcrumb: {
          title: 'Report Cards',
          parent: '/admin/dashboard',
        },
      },
    ],
  },
  {
    isGroup: true,
    groupName: 'Teacher Actions',
    name: 'Teacher Actions',
    icon: ClipboardList,
    showOnSideBar: true,
    state: 'adminteacheractions',
    children: [
      {
        path: '/admin/teacher-actions/take-attendance',
        name: 'Teacher Attendance',
        icon: CalendarCheck,
        showOnSideBar: true,
        component: AdminTeacherTakeAttendance,
        isCombobox: true,
        permission: AdminPermissions.teacherAttendance,
        breadcrumb: {
          title: 'Teacher Attendance',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/admin/teacher-actions/attendance-sheet',
        name: 'Teacher Attendance Sheet',
        icon: Sheet,
        showOnSideBar: true,
        component: AdminTeacherAttendanceSheet,
        permission: AdminPermissions.teacherAttendanceSheet,
        isCombobox: true,
        breadcrumb: {
          title: 'Teacher Attendance Sheet',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/admin/teacher-actions/update-attendance',
        name: 'Update Teacher Attendance',
        icon: CalendarClock,
        showOnSideBar: true,
        component: AdminTeacherUpdateAttendance,
        permission: AdminPermissions.teacherUpdateAttendance,
        isCombobox: true,
        breadcrumb: {
          title: 'Update Teacher Attendance',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/admin/teacher-actions/payroll',
        name: 'Teacher Payroll',
        icon: Receipt,
        showOnSideBar: true,
        // component: AdminTeacherPayroll,
        component: TempPage,
        isCombobox: true,
        breadcrumb: {
          title: 'Teacher Payroll',
          parent: '/admin/dashboard',
        },
      },
    ],
  },
  {
    path: '/academics/discipline/',
    name: 'Discipline Records',
    icon: FileText,
    showOnSideBar: true,
    component: DisciplineRecords,
    isCombobox: true,
    permission: AdminPermissions.disciplineRecord,
    breadcrumb: {
      title: 'Discipline Records',
      parent: '/admin/dashboard',
    },
  },
  {
    isLabel: true,
    name: 'Finance',
    showOnSideBar: true,
  },
  {
    path: '/school-admin/finance/edit-bus-fees/:id',
    name: 'Edit Bus Fees',
    showOnSideBar: false,
    component: TempPage,
    isCombobox: false,
    breadcrumb: {
      title: 'Edit Bus Fees',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/school-admin/finance/edit-canteen-fees/:id',
    name: 'Edit Canteen Fees',
    showOnSideBar: false,
    component: TempPage,
    isCombobox: false,
    breadcrumb: {
      title: 'Edit Canteen Fees',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/school-admin/finance/edit-school-fees/:id',
    name: 'Edit School Fees',
    showOnSideBar: false,
    component: TempPage,
    isCombobox: false,
    breadcrumb: {
      title: 'Edit School Fees',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/school-admin/finance/pending-fees/soa/:id',
    name: 'Statement of Account',
    showOnSideBar: false,
    component: AdminSoa,
    isCombobox: false,
    breadcrumb: {
      title: 'Statement of Account',
      parent: '/admin/dashboard',
    },
  },
  {
    isGroup: true,
    groupName: 'School Fees',
    name: 'School Fees',
    icon: FileText,
    showOnSideBar: true,
    state: 'feespages',
    permission: AdminPermissions.schoolFees,
    children: [
      // {
      //   path: '/finance/define-rates/',
      //   icon: FileText,
      //   name: 'Define Rates',
      //   showOnSideBar: true,
      //   component: DefineRates,
      //   isCombobox: false,
      //   breadcrumb: {
      //     title: 'Define Rates',
      //     parent: '/admin/dashboard',
      //   },
      // },
      // {
      //   path: '/finance/fee-structure/',
      //   icon: FileText,
      //   name: 'Fee Structure',
      //   showOnSideBar: true,
      //   component: FeeStructure,
      //   isCombobox: false,
      //   breadcrumb: {
      //     title: 'Fee Structure',
      //     parent: '/admin/dashboard',
      //   },
      // },
      {
        path: '/finance/pending-fees/',
        icon: FileText,
        name: 'Pending Fees',
        showOnSideBar: true,
        component: PendingFees,
        isCombobox: false,
        breadcrumb: {
          title: 'Pending Fees',
          parent: '/admin/dashboard',
        },
      },
    ],
  },
  {
    path: '/finance/cash-deposit/',
    name: 'Cash Deposit',
    icon: Coins,
    showOnSideBar: true,
    component: CashDeposit,
    permission: AdminPermissions.cashDeposit,
    isCombobox: false,
    breadcrumb: {
      title: 'Cash Deposit',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/leave-request/:person/:id',
    name: 'Leave Request',
    showOnSideBar: false,
    component: TempPage,
    isCombobox: false,
    breadcrumb: {
      title: 'Leave Request',
      parent: '/admin/dashboard',
    },
  },
  {
    isLabel: true,
    name: 'Leaves',
    showOnSideBar: true,
  },
  {
    isGroup: true,
    groupName: 'Leaves',
    name: 'Leaves',
    icon: Calendar,
    showOnSideBar: true,
    state: 'leavespages',
    permission: AdminPermissions.leaves,
    children: [
      // {
      //   path: '/leave/staff/',
      //   icon: Calendar,
      //   name: 'Staff Leaves',
      //   showOnSideBar: true,
      //   component: AdminStaffLeaves,
      //   isCombobox: false,
      //   breadcrumb: {
      //     title: 'Staff Leaves',
      //     parent: '/admin/dashboard',
      //   },
      // },
      {
        path: '/leave/student/',
        icon: Calendar,
        name: 'Student Leaves',
        showOnSideBar: true,
        component: AdminStudentLeaves,
        isCombobox: false,
        breadcrumb: {
          title: 'Student Leaves',
          parent: '/admin/dashboard',
        },
      },
      {
        path: '/leave/teacher/',
        icon: Calendar,
        name: 'Teacher Leaves',
        showOnSideBar: true,
        component: AdminTeacherLeaves,
        isCombobox: false,
        breadcrumb: {
          title: 'Teacher Leaves',
          parent: '/admin/dashboard',
        },
      },
    ],
  },
  {
    isLabel: true,
    name: 'Reports',
    showOnSideBar: true,
  },
  {
    path: '/reports/',
    name: 'School Reports',
    icon: FileText,
    showOnSideBar: true,
    component: AdminReports,
    isCombobox: false,
    permission: AdminPermissions.reports,
    breadcrumb: {
      title: 'School Reports',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/reports/ranking-report/',
    name: 'Ranking Report',
    icon: FileText,
    showOnSideBar: false,
    component: RankingReport,
    isCombobox: false,
    permission: AdminPermissions.reports,
    breadcrumb: {
      title: 'Ranking Report',
      parent: '/reports/',
    },
  },
  {
    isLabel: true,
    name: 'MISCELLANEOUS',
    showOnSideBar: true,
  },
  {
    path: '/library',
    name: 'Library',
    icon: BookOpen,
    showOnSideBar: true,
    component: AdminLibrary,
    isCombobox: false,
    breadcrumb: {
      title: 'Library',
      parent: '/admin/dashboard',
    },
  },
  //   {
  //     path: '/library/import',
  //     name: 'Bulk Upload Books',
  //     icon: FileText,
  //     showOnSideBar: true,
  //     component: AdminLibraryImport,
  //     isCombobox: false,
  //     breadcrumb: {
  //       title: 'Bulk Upload Books',
  //       parent: '/admin/dashboard',
  //     },
  //   },
  {
    path: '/inventory-item/:id',
    name: 'Inventory Item',
    showOnSideBar: false,
    component: AdminInventory,
    isCombobox: false,
    breadcrumb: {
      title: 'Inventory Item',
      parent: '/inventory',
    },
  },
  {
    path: '/inventory',
    name: 'Inventory',
    icon: FileText,
    showOnSideBar: true,
    component: AdminInventory,
    isCombobox: true,
    breadcrumb: {
      title: 'Inventory',
      parent: '/admin/dashboard',
    },
  },
  {
    path: '/',
    name: 'Dashboard Redirect',
    component: TempPage,
    showOnSideBar: false,
    isCombobox: false,
  },
];
